import {
  useInfiniteQuery,
  infiniteQueryOptions,
  useQuery,
  queryOptions,
  keepPreviousData,
} from '@tanstack/react-query';
import { api } from '@/api';
import { PaginatedBody, SortOrderQuery } from '@/api/types';
import { EmployeeComposed, PaginatedEmployeesListFilters } from '@/api/apis/resources';
import {
  getComposedEmployeeData,
  getEmployeesByIds,
  seedComposedEmployeeFromList,
  seedEmployeeFromList,
} from '@/queries/employee';
import { queryClient } from '@/queries';
import { BASE_QUERY_KEYS } from '@/queries/consts';
import { airportsQuery } from '@/queries/airports';
import { DEFAULT_INFINITE_QUERY_OPTIONS } from '../utils';
import { getHomeLocationByIds } from '../homelocation';
import { employeesQueryKeys } from './employee.keys';

const EMPLOYEE_DATA_STALE_TIME = 60 * 1000 * 10; // 10 min

export const getComposedEmployeeById = async ({
  userId,
  employeeId = '',
}: {
  userId?: string;
  employeeId?: string;
}): Promise<EmployeeComposed> => {
  const employee = userId
    ? await api.resource.getEmployeeByUserId(userId)
    : await api.resource.getEmployeeById(employeeId);

  return getComposedEmployeeData(employee);
};

export const getComposedEmployees = async (
  params: PaginatedEmployeesListFilters,
): Promise<PaginatedBody<EmployeeComposed>> => {
  const data = await api.resource.getEmployeeList(params);
  seedEmployeeFromList(data.items);

  if (data && data.items.length > 0) {
    const homeLocationList = data.items.map((e) => e.homeLocation).filter(Boolean);
    const airportsIds = Array.from(new Set(data.items.map((e) => e.closestAirportForDeployment).filter(Boolean)));
    await getHomeLocationByIds(homeLocationList);
    await queryClient.ensureQueryData(
      airportsQuery.items(
        airportsIds.map((e) => `${e}`),
        'maoIds',
      ),
    );
  }

  const items = await Promise.all(data.items.map((e) => getComposedEmployeeData(e)));
  const result = { ...data, items };
  seedComposedEmployeeFromList(result.items);

  return result;
};

export const employeeQueries = {
  listByIds: (ids: string[]) =>
    queryOptions({
      queryKey: employeesQueryKeys.getList(ids),
      queryFn: async () => {
        const resp = await getEmployeesByIds(ids);
        seedEmployeeFromList(resp?.items);
        return resp;
      },
      staleTime: EMPLOYEE_DATA_STALE_TIME,
    }),
  infiniteList: (searchPagination: PaginatedEmployeesListFilters, type: string = 'employee') =>
    infiniteQueryOptions({
      queryKey: employeesQueryKeys.getInfiniteList(type, searchPagination),
      queryFn: ({ pageParam: pageIndex }) => api.resource.getEmployeeList({ ...searchPagination, pageIndex }),
      placeholderData: keepPreviousData,
      ...DEFAULT_INFINITE_QUERY_OPTIONS,
    }),
  users: ({
    enabled,
    keepPrevious = true,
    ...listQuery
  }: PaginatedEmployeesListFilters & {
    enabled?: boolean;
    keepPrevious?: boolean;
  }) =>
    queryOptions({
      queryKey: employeesQueryKeys.getList('users', listQuery),
      queryFn: () => getComposedEmployees(listQuery),
      placeholderData: keepPrevious ? keepPreviousData : undefined,
      enabled,
    }),
  employee: (employeeId: string) =>
    queryOptions({
      queryKey: employeesQueryKeys.getItem(employeeId, BASE_QUERY_KEYS.composed),
      queryFn: () => getComposedEmployeeById({ employeeId }),
      enabled: !!employeeId,
      staleTime: EMPLOYEE_DATA_STALE_TIME,
    }),
  employeeByUserId: (userId: string) =>
    queryOptions({
      queryKey: employeesQueryKeys.getItem(userId, BASE_QUERY_KEYS.composed),
      queryFn: () => getComposedEmployeeById({ userId }),
      enabled: !!userId,
      staleTime: EMPLOYEE_DATA_STALE_TIME,
    }),
  item: (employeeId: string) =>
    queryOptions({
      queryKey: employeesQueryKeys.getItem(employeeId),
      queryFn: () => api.resource.getEmployeeById(employeeId),
      staleTime: EMPLOYEE_DATA_STALE_TIME,
    }),
  itemByUserId: (userId: string) =>
    queryOptions({
      queryKey: employeesQueryKeys.getItem(userId),
      queryFn: () => api.resource.getEmployeeByUserId(userId),
      staleTime: EMPLOYEE_DATA_STALE_TIME,
    }),
};

const useEmployeesListQuery = (
  listQuery: PaginatedEmployeesListFilters,
  options: {
    enabled?: boolean;
    keepPrevious?: boolean;
  } = {},
) => {
  return useQuery(employeeQueries.users({ ...listQuery, ...options }));
};

const useInfiniteEmployeeListQuery = (filters?: PaginatedEmployeesListFilters & SortOrderQuery) => {
  return useInfiniteQuery(employeeQueries.infiniteList({ itemsPerPage: 20, pageIndex: 0, ...filters }));
};

const useInfiniteManagerListQuery = (
  filters?: PaginatedEmployeesListFilters,
  options: {
    enabled?: boolean;
  } = {},
) => {
  return useInfiniteQuery({ ...employeeQueries.infiniteList({ ...filters }, 'nationalManager'), ...options });
};

const useComposedEmployeeByIdQuery = (id: string) => {
  return useQuery(employeeQueries.employee(id));
};

export {
  useInfiniteEmployeeListQuery,
  useInfiniteManagerListQuery,
  useEmployeesListQuery,
  useComposedEmployeeByIdQuery,
};
